import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import usePasswordReset from "../../api/passwordReset";
import { useTheme } from "../../context/ThemeContext";
import { AlertCircle, Check, ArrowLeft, Sun, Moon } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { requestReset } = usePasswordReset();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email) {
      setError("Email is verplicht");
      return;
    }
    setLoading(true);
    try {
      await requestReset(email);
      setSuccess(
        "Als dit email bestaat is er een reset link verzonden. Controleer je inbox.",
      );
      setEmail("");
    } catch (e) {
      // Still show success message to avoid enumeration
      setSuccess(
        "Als dit email bestaat is er een reset link verzonden. Controleer je inbox.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-6 transition-colors duration-200 ${isDark ? "bg-[#0a0a0a]" : "bg-[#f8f8fa]"}`}
    >
      <div className="w-full max-w-[400px]">
        {/* Logo + theme toggle */}
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center">
            <span
              className={`text-[15px] font-zichtbaar font-black tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
            >
              ZICHTBAAR
            </span>
          </Link>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-lg transition-colors ${isDark ? "text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Card */}
          <div
            className={`rounded-xl p-6 space-y-5 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            {/* Header */}
            <div>
              <h2
                className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Wachtwoord vergeten
              </h2>
              <p
                className={`text-[13px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Vul je email in en we sturen een link om je wachtwoord te
                resetten.
              </p>
            </div>

            {/* Error */}
            {error && (
              <div
                className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
              >
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Success */}
            {success && (
              <div
                className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px] ${isDark ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}
              >
                <Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="uw@bedrijf.be"
                  className={`w-full h-10 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.08] text-white placeholder-gray-600 focus:border-white/[0.15]" : "bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full h-10 rounded-lg text-[13px] font-medium transition-all duration-150 ${loading ? "opacity-70 cursor-not-allowed" : ""} ${isDark ? "bg-[#1E4FCF] text-white hover:bg-[#2a5fd9]" : "bg-[#BFFF00] text-black hover:bg-[#d4ff4d]"}`}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className={`h-3.5 w-3.5 rounded-full border-2 animate-spin ${isDark ? "border-white/20 border-t-white/60" : "border-black/20 border-t-black/60"}`}
                    />
                    <span>Versturen...</span>
                  </div>
                ) : (
                  "Verstuur reset link"
                )}
              </button>
            </form>

            {/* Back link */}
            <button
              onClick={() => navigate("/login")}
              className={`flex items-center gap-1.5 text-[12px] font-medium transition-colors ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
            >
              <ArrowLeft size={13} />
              Terug naar login
            </button>
          </div>
        </motion.div>

        <p
          className={`mt-8 text-center text-[11px] ${isDark ? "text-gray-700" : "text-gray-400"}`}
        >
          &copy; {new Date().getFullYear()} Zichtbaar Dashboard
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
